import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, Card, Empty, Spinner } from "@/components/UIKit";
import StatusBadge from "@/components/StatusBadge";
import { Plus, Search, Users, Building2, Filter } from "lucide-react";

export default function Kunder() {
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const t = setTimeout(async () => {
      setItems(null);
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (kind) params.set("kind", kind);
      params.set("limit", "100");
      const { data } = await api.get(`/customers?${params}`);
      setItems(data.items); setTotal(data.total);
    }, 200);
    return () => clearTimeout(t);
  }, [q, kind]);

  return (
    <div data-testid="kunder-page">
      <PageHeader
        title="Kunder"
        subtitle={`${total} registrerte kunder`}
        actions={
          <button
            onClick={() => navigate("/kunder/ny")}
            className="h-9 px-3.5 rounded-md bg-emerald-500 hover:bg-emerald-400 text-black text-sm font-medium flex items-center gap-2 transition-colors"
            data-testid="ny-kunde-btn"
          >
            <Plus className="h-4 w-4" /> Ny kunde
          </button>
        }
      />

      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-lg">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Søk navn, kundenr, orgnr, e-post…"
            className="w-full h-9 pl-9 pr-3 rounded-md bg-zinc-900/60 border border-zinc-900 focus:border-zinc-800 text-sm text-zinc-100 placeholder-zinc-500 focus:outline-none"
            data-testid="kunder-search"
          />
        </div>
        <div className="flex items-center gap-1 bg-zinc-900/60 rounded-md p-1 border border-zinc-900">
          {[
            { v: "", label: "Alle", icon: Filter },
            { v: "privat", label: "Privat", icon: Users },
            { v: "bedrift", label: "Bedrift", icon: Building2 },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setKind(f.v)}
              className={`px-2.5 py-1 rounded text-xs flex items-center gap-1.5 transition-colors ${
                kind === f.v ? "bg-zinc-800 text-zinc-100" : "text-zinc-500 hover:text-zinc-200"
              }`}
              data-testid={`filter-${f.v || "all"}`}
            >
              <f.icon className="h-3 w-3" /> {f.label}
            </button>
          ))}
        </div>
      </div>

      <Card testid="kunder-table" className="overflow-hidden">
        {items === null ? (
          <div className="p-8"><Spinner /></div>
        ) : items.length === 0 ? (
          <Empty title="Ingen kunder funnet" subtitle="Prøv å endre søkekriteriene" />
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-zinc-500 bg-zinc-950/60 sticky top-0">
                <th className="py-2.5 px-4 font-medium">Kundenr</th>
                <th className="py-2.5 px-4 font-medium">Navn</th>
                <th className="py-2.5 px-4 font-medium">Type</th>
                <th className="py-2.5 px-4 font-medium">Kontakt</th>
                <th className="py-2.5 px-4 font-medium">By</th>
                <th className="py-2.5 px-4 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => navigate(`/kunder/${c.id}`)}
                  className="border-t border-zinc-900 hover:bg-zinc-900/40 cursor-pointer transition-colors"
                  data-testid={`kunde-row-${c.customer_number}`}
                >
                  <td className="py-2.5 px-4 font-mono text-xs text-emerald-400">{c.customer_number}</td>
                  <td className="py-2.5 px-4 text-zinc-100 font-medium">{c.full_name}</td>
                  <td className="py-2.5 px-4">
                    <span className="inline-flex items-center gap-1 text-xs text-zinc-400">
                      {c.kind === "bedrift" ? <><Building2 className="h-3 w-3" /> Bedrift</> : <><Users className="h-3 w-3" /> Privat</>}
                    </span>
                  </td>
                  <td className="py-2.5 px-4 text-zinc-400 text-xs">{c.email || "—"}<br />{c.phone || ""}</td>
                  <td className="py-2.5 px-4 text-zinc-400 text-xs">{c.postal_code} {c.city}</td>
                  <td className="py-2.5 px-4"><StatusBadge value={c.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}
